import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { VehicleInfo, VehicleInput } from "../../../generated/nest-graphql";
import { describeVehicle, mileageInspectionText, vehicleFromJobInfo } from "../vehicleHistoryData";
import { useVehicleHistory } from "../../../hooks/useVehicleHistory";
import { useVehicleJobHistory } from "../../../hooks/useVehicleJobHistory";
import { VehicleHistoryHeader } from "../VehicleHistoryHeader";
import {
  VehicleHistoryTab,
  VehicleHistoryTabs,
  vehicleHistoryPanelId,
  vehicleHistoryTabId,
} from "../VehicleHistoryTabs";
import { ServiceHistoryList, ServiceSource, SourceToggle } from "../ServiceHistoryList";
import { VehicleSpecs, latestInspection } from "../VehicleSpecs";
import { LoadingMessage } from "../LoadingMessage";
import { useUpdateVehicle } from "../../../hooks/useUpdateVehicle";
import { VehicleSaveScope } from "../../../hooks/useSyncJobVehicleInfo";
import { useDispatch } from "../../../contexts/snackbar-context";
import { showSuccessAlert, showWarningAlert } from "../../../actions";
import { CARFAX_REFRESH_ENABLED } from "../../../lib/vehicleHistory.utils";
import { EditVehicleDialog, canSaveMileageOnly, notesUpdateInput, vehicleNotes } from "../EditVehicleDialog";
import { EditNotesDialog } from "../EditNotesDialog";
import { DeadEnd } from "./DeadEnd";
import { useBrandColors } from "../../ui";

export type VehicleHistoryUiState = { tab: VehicleHistoryTab; source: ServiceSource };

export const DEFAULT_VEHICLE_HISTORY_UI_STATE: VehicleHistoryUiState = {
  tab: VehicleHistoryTab.SPECS,
  source: ServiceSource.NUBRAKES,
};

/** VEHICLE-HISTORY (NUB-358): identical in both mounts, so only navigation differs. */
const VehicleHistoryViewInner: React.FC<{
  vehicleId?: string | null;
  /** The job's embedded vehicle, used when there is no vehicle record so older jobs still match by VIN or plate. */
  fallbackVehicleInfo?: VehicleInfo | null;
  contactId?: string | null;
  currentJobId?: string | null;
  /** Restores the section and toggles on mount. Read once; a getter so a parent that keeps updating it does not re-render the view. */
  getInitialUiState?: () => VehicleHistoryUiState | undefined;
  onUiStateChange?: (state: VehicleHistoryUiState) => void;
  /** For a parent that keeps the view mounted while hidden: each time it is shown again its lists are re-read. */
  visible?: boolean;
  /**
   * Distance from the top of the window where the page's own sticky header ends. When given, the vehicle header
   * and section tabs stick right below it, so only the section's records scroll.
   */
  stickyTop?: number;
  /** Called once the lists have loaded (or a dead end shows), for a parent waiting on content to restore scroll. */
  onLoaded?: () => void;
  /** After the vehicle is saved: the job page brings its own copy of the vehicle in line. Rejects on failure. */
  onVehicleSaved?: (vehicle: VehicleInput, scope?: VehicleSaveScope) => Promise<void>;
}> = ({
  vehicleId,
  fallbackVehicleInfo,
  contactId,
  currentJobId,
  getInitialUiState,
  onUiStateChange,
  visible = true,
  onLoaded,
  stickyTop,
  onVehicleSaved,
}) => {
  const [ui, setUi] = useState<VehicleHistoryUiState>(() => getInitialUiState?.() ?? DEFAULT_VEHICLE_HISTORY_UI_STATE);
  useEffect(() => onUiStateChange?.(ui), [ui, onUiStateChange]);

  const { pageBackground } = useBrandColors();
  const vehicleHistory = useVehicleHistory(vehicleId);
  const { save: saveVehicle } = useUpdateVehicle();
  const dispatch = useDispatch();
  const [editingVehicle, setEditingVehicle] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const { carfax, carfaxUpdatedAt, requestedAt, loading, refreshing, refreshError, refreshCarfax } = vehicleHistory;
  // Keyed on the fields, not the object: every job refetch hands over a new vehicleInfo, and a new vehicle
  // would rerun the matching and grouping below over every job.
  const info = fallbackVehicleInfo;
  // Carries the job's vehicle id too, so jobs linked to the car only by id still match when the record failed.
  const fallbackVehicle = useMemo(
    () => {
      const fromInfo = vehicleFromJobInfo(info);
      return fromInfo && vehicleId ? { ...fromInfo, id: vehicleId } : fromInfo;
    },
    [vehicleId, info?.year, info?.make, info?.model, info?.vin, info?.licensePlate, info?.licensePlateState] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // The record wins, but the job's own details fill in what the record lacks, so older jobs still match and
  // the header names the car the Details tab does.
  const record = vehicleHistory.vehicle;
  const vehicle = useMemo(
    () =>
      record
        ? {
            ...record,
            // From the job's info directly: a job with no VIN or plate still names its car.
            year: record.year || info?.year,
            make: record.make || info?.make,
            model: record.model || info?.model,
            vin: record.vin || fallbackVehicle?.vin,
            // A state belongs to its plate, so it only comes along when the plate does.
            ...(record.licensePlate
              ? {}
              : { licensePlate: fallbackVehicle?.licensePlate, licensePlateState: fallbackVehicle?.licensePlateState }),
          }
        : fallbackVehicle,
    [record, fallbackVehicle, info?.year, info?.make, info?.model] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Only a vehicle record with its owner can be saved; a car known only from a job's own details cannot. The form
  // opens on what the header shows, so a record missing its year, make, model, VIN or plate starts from the job's.
  const editableVehicle = record?.id && record.contactId && vehicle ? vehicle : null;

  // The vehicle knows its owner, so a caller that only has a vehicle id still works.
  const ownerId = contactId ?? vehicleHistory.vehicle?.contactId ?? null;
  // A vehicle id whose record no longer exists is no vehicle at all, unless the job's own VIN or plate stands in.
  const hasVehicle = !!vehicle || (!!vehicleId && !vehicleHistory.missing);
  // Without a vehicle there is nothing to match the customer's jobs against.
  const jobHistory = useVehicleJobHistory(hasVehicle ? ownerId : null, vehicle);
  const inspection = useMemo(
    () => latestInspection(jobHistory.vehicleJobs, currentJobId),
    [jobHistory.vehicleJobs, currentJobId]
  );
  // The record's own mileage is shown as is; the jobs only name the inspection that recorded it.
  const mileageSource = useMemo(
    () => mileageInspectionText(jobHistory.vehicleJobs, record?.odometer),
    [jobHistory.vehicleJobs, record?.odometer]
  );

  // The job list is no-cache, so nothing else updates it: a job closed on Details meanwhile shows only on a
  // re-read. Apollo merges identical requests already in flight, so quick tab switching does not stack them.
  const wasVisible = useRef(visible);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const { refetch: refetchJobs } = jobHistory;
  const { refetch: refetchVehicle } = vehicleHistory;
  useEffect(() => {
    if (visible && !wasVisible.current) {
      if (hasVehicle && ownerId) refetchJobs().catch(() => undefined);
      if (vehicleId) refetchVehicle().catch(() => undefined);
    }
    wasVisible.current = visible;
  }, [visible, hasVehicle, ownerId, vehicleId, refetchJobs, refetchVehicle]);

  const settled = !loading && !jobHistory.loading;
  useEffect(() => {
    if (settled) onLoaded?.();
  }, [settled, onLoaded]);

  if (!hasVehicle) {
    return (
      <DeadEnd
        message={
          vehicleHistory.missing
            ? currentJobId
              ? "This job's vehicle record no longer exists, so there is no vehicle history to show."
              : "This vehicle record no longer exists."
            : describeVehicle(fallbackVehicleInfo)
            ? "This job's vehicle has no VIN or plate, so its history can't be matched."
            : "No vehicle is recorded on this job, so there is no vehicle history to show."
        }
        contactId={ownerId}
        jobId={currentJobId}
      />
    );
  }

  // With the job's own VIN or plate to match on, a missing vehicle record only costs Carfax.
  const vehicleFailed = !!vehicleHistory.error && !vehicleHistory.missing && !fallbackVehicle;
  if (vehicleFailed) {
    return (
      <DeadEnd
        message="This vehicle's history could not be loaded."
        onRetry={() => Promise.all([refetchVehicle(), jobHistory.error && refetchJobs()].filter(Boolean))}
        contactId={ownerId}
        jobId={currentJobId}
      />
    );
  }

  // A vehicle with no customer on record cannot have its jobs looked up, which is not the same as having none.
  if (!ownerId && !loading) {
    return <DeadEnd message="This vehicle has no customer on record, so its job history can't be loaded." />;
  }

  const carfaxPending = refreshing || !!requestedAt;
  // The resolver asks Carfax by VIN, so without one a refresh can only fail.
  const canRefreshCarfax = CARFAX_REFRESH_ENABLED && !!vehicleId && !!vehicleHistory.vehicle?.vin;

  const afterVehicleSaved = (input: VehicleInput) => {
    showSuccessAlert(dispatch, "Vehicle updated");
    // The save's own result updates the record; the re-read just confirms it, in the background.
    refetchVehicle().catch(() => undefined);
    // The vehicle is saved either way; only the job's copy may lag, so say so without undoing anything.
    onVehicleSaved?.(input, VehicleSaveScope.ALL).catch(() =>
      showWarningAlert(dispatch, "Vehicle updated, but this job's vehicle details could not be updated")
    );
  };

  return (
    <div className="flex flex-col">
      {/* Pinned with the page's own background, so the header keeps its look and records don't show through. */}
      <div
        className={stickyTop === undefined ? undefined : "sticky z-10"}
        style={stickyTop === undefined ? undefined : { top: stickyTop, backgroundColor: pageBackground }}
        data-testid="vehicle-history-sticky-header"
        ref={stickyHeaderRef}
      >
        <VehicleHistoryHeader
          vehicle={vehicle}
          // A Carfax check writes the whole record back when it answers, which would undo an edit saved meanwhile.
          // Partial: an answer later than the re-checks can still do it, until the backend writes only Carfax's fields.
          onEdit={editableVehicle && !carfaxPending ? () => setEditingVehicle(true) : undefined}
          editHint={editableVehicle && carfaxPending ? "Editing unlocks once the Carfax check finishes." : undefined}
        />
        <div className="bg-[#1f2b3a] border-b border-[#44576e]">
          <VehicleHistoryTabs value={ui.tab} onChange={(tab) => setUi((current) => ({ ...current, tab }))} />
        </div>
        {/* The Service History source toggle pins with the rest, so only the records scroll. */}
        {ui.tab === VehicleHistoryTab.SERVICES && (
          <SourceToggle
            value={ui.source}
            onChange={(source) => setUi((current) => ({ ...current, source }))}
            counts={{
              [ServiceSource.NUBRAKES]: jobHistory.services.length,
              [ServiceSource.CARFAX]: carfax.length,
            }}
          />
        )}
      </div>

      <div role="tabpanel" id={vehicleHistoryPanelId(ui.tab)} aria-labelledby={vehicleHistoryTabId(ui.tab)}>
        {/* Specs come from the vehicle record, so only Service History waits on, or fails with, the jobs. */}
        {loading || (ui.tab === VehicleHistoryTab.SERVICES && jobHistory.loading) ? (
          <div className="p-6 flex justify-center">
            <LoadingMessage>Loading this vehicle&apos;s history…</LoadingMessage>
          </div>
        ) : ui.tab === VehicleHistoryTab.SERVICES && jobHistory.error ? (
          <DeadEnd
            message="This vehicle's service history could not be loaded."
            onRetry={refetchJobs}
            contactId={ownerId}
            jobId={currentJobId}
          />
        ) : (
          <>
            {ui.tab === VehicleHistoryTab.SERVICES && (
              <ServiceHistoryList
                pinnedHeaderRef={stickyHeaderRef}
                source={ui.source}
                onSourceChange={(source) => setUi((current) => ({ ...current, source }))}
                showSourceToggle={false}
                services={jobHistory.services}
                carfax={carfax}
                carfaxUpdatedAt={carfaxUpdatedAt}
                requestedAt={requestedAt}
                refreshing={refreshing}
                refreshError={refreshError}
                onRefreshCarfax={canRefreshCarfax ? refreshCarfax : undefined}
                noRefreshReason={
                  vehicleHistory.missing
                    ? "This job's vehicle record no longer exists, so Carfax is unavailable."
                    : vehicleHistory.error
                    ? "The vehicle record could not be loaded, so Carfax is unavailable."
                    : vehicleId && vehicleHistory.vehicle?.vin
                    ? "No Carfax records stored."
                    : vehicleId
                    ? "The vehicle record has no VIN, so Carfax can't be checked. Add the VIN to the vehicle."
                    : undefined
                }
                currentJobId={currentJobId}
                onRetryVehicle={vehicleHistory.error && !vehicleHistory.missing ? refetchVehicle : undefined}
              />
            )}
            {ui.tab === VehicleHistoryTab.SPECS && (
              <VehicleSpecs
                vehicle={vehicle}
                inspection={inspection}
                mileageSource={mileageSource}
                // The save needs a year, make and model, and must not race a Carfax answer, like the edit form.
                onEditNotes={
                  editableVehicle && record && canSaveMileageOnly(record, vehicle) && !carfaxPending
                    ? () => setEditingNotes(true)
                    : undefined
                }
                // The job's own VIN or plate can stand in for matching history, but it carries no specs.
                unavailableReason={
                  vehicleHistory.missing
                    ? "This job's vehicle record no longer exists, so its specs are unavailable."
                    : vehicleHistory.error
                    ? "The vehicle record could not be loaded, so its specs are unavailable."
                    : !vehicleId
                    ? "No vehicle record is linked to this job, so there are no specs to show."
                    : null
                }
                onRetry={vehicleHistory.error && !vehicleHistory.missing ? refetchVehicle : undefined}
              />
            )}
          </>
        )}
      </div>
      {editableVehicle && record && (
        <EditNotesDialog
          open={editingNotes}
          notes={vehicleNotes(record)}
          onCancel={() => setEditingNotes(false)}
          onSave={async (notes) => {
            // The record's own values, as the edit form saves them, with only the notes changed.
            await saveVehicle(notesUpdateInput(record, notes, vehicle));
            setEditingNotes(false);
            showSuccessAlert(dispatch, "Notes saved");
            refetchVehicle().catch(() => undefined);
          }}
        />
      )}
      {editableVehicle && (
        <EditVehicleDialog
          open={editingVehicle}
          vehicle={editableVehicle}
          stored={record}
          mileageSource={mileageSource}
          onCancel={() => setEditingVehicle(false)}
          onSave={async (input) => {
            await saveVehicle(input);
            setEditingVehicle(false);
            afterVehicleSaved(input);
          }}
        />
      )}
    </div>
  );
};

// Kept mounted while hidden under the job page, so it must not re-render with every page render.
export const VehicleHistoryView = React.memo(VehicleHistoryViewInner);
